import { sidebarData } from "@/app/layout/data/sidebar-data";
import type {
  NavCollapsible,
  NavItem,
  NavLink,
  SidebarData,
} from "@/app/layout/types";
import { useMemo } from "react";
import type { AdminNotificationId } from "../../domain/types";
import { useAdminNotificationsQuery } from "../api/queries";
import { formatNotificationBadge } from "../lib/notification-links";

function applyBadge(item: NavItem, counts: Map<AdminNotificationId, number>): NavItem {
  if ("url" in item && item.url && item.notificationId) {
    const count = counts.get(item.notificationId) ?? 0;
    const link: NavLink = {
      ...item,
      badge: count > 0 ? formatNotificationBadge(count) : undefined,
    };
    return link;
  }

  if ("items" in item && item.items) {
    const collapsible: NavCollapsible = {
      ...item,
      items: item.items.map((sub) => {
        if (!sub.notificationId) return sub;
        const count = counts.get(sub.notificationId) ?? 0;
        return {
          ...sub,
          badge: count > 0 ? formatNotificationBadge(count) : undefined,
        };
      }),
    };
    const subtotal = collapsible.items.reduce((sum, sub) => {
      if (!sub.notificationId) return sum;
      return sum + (counts.get(sub.notificationId) ?? 0);
    }, 0);
    return {
      ...collapsible,
      badge: subtotal > 0 ? formatNotificationBadge(subtotal) : undefined,
    };
  }

  return item;
}

export function useSidebarWithNotifications(): SidebarData {
  const { data } = useAdminNotificationsQuery();

  return useMemo(() => {
    const counts = new Map<AdminNotificationId, number>();
    for (const item of data?.items ?? []) {
      counts.set(item.id, item.count);
    }

    return {
      navGroups: sidebarData.navGroups.map((group) => ({
        ...group,
        items: group.items.map((item) => applyBadge(item, counts)),
      })),
    };
  }, [data]);
}
